// Document Signer — iPad-safe field filling and signature capture.
// The PDF renders once per page and never re-renders during typing —
// typing happens in a bottom-sheet modal (`FieldEditorSheet`) so the
// iOS keyboard can't glitch out an active input floating over the PDF.
// Every field change autosaves via a debounced PATCH.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { BACKEND_URL } from "../api";
import { toast } from "sonner";
import { PdfCanvas } from "../components/documents/PdfCanvas";
import FieldEditorSheet from "../components/documents/FieldEditorSheet";
import SignaturePadModal from "../components/documents/SignaturePadModal";
import {
  ArrowLeft, ArrowRight, CircleNotch, CheckCircle, Signature as SignatureIcon,
  FloppyDisk, Lock, DownloadSimple, WarningCircle,
} from "@phosphor-icons/react";

export default function DocumentSigner() {
  const { did } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [values, setValues] = useState({});
  const [signature, setSignature] = useState(null);
  const [signatureFieldId, setSignatureFieldId] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [pageWidth, setPageWidth] = useState(900);
  const [pageSize, setPageSize] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [signing, setSigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [completing, setCompleting] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/documents/${did}`);
      setDoc(data);
      setValues(data.values || {});
      setSignature(data.signature_png || null);
      setSignatureFieldId(data.signature_field_id || null);
    } catch (e) {
      toast.error("Document not found"); nav("/admin/documents");
    }
  }, [did, nav]);
  useEffect(() => { load(); }, [load]);

  // Pull the template row for its `pdf_url` and `page_count` — the
  // document row only carries a snapshot of the field list.
  useEffect(() => {
    if (!doc?.template_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/document-templates/${doc.template_id}`);
        if (!cancelled) setTpl(data);
      } catch { /* ignore — signer shows loading state */ }
    })();
    return () => { cancelled = true; };
  }, [doc?.template_id]);

  // Responsive rendering width: iPad landscape gets a wider PDF,
  // portrait mobile scales down. We recompute on window resize.
  useEffect(() => {
    const compute = () => {
      const w = Math.min(window.innerWidth - 60, 900);
      setPageWidth(Math.max(320, w));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const fields = doc?.template_fields_snapshot || [];
  const pageFields = useMemo(() => fields.filter((f) => f.page === pageIdx), [fields, pageIdx]);
  const requiredIds = useMemo(() => fields.filter((f) => f.required).map((f) => f.id), [fields]);
  const completedCount = useMemo(() => requiredIds.filter((id) => {
    const f = fields.find((x) => x.id === id);
    if (!f) return false;
    if (f.type === "signature") return !!signature;
    if (f.type === "checkbox") return !!values[id];
    return values[id] != null && String(values[id]).trim() !== "";
  }).length, [requiredIds, fields, values, signature]);
  const totalRequired = requiredIds.length;
  const readyToSign = totalRequired > 0 && completedCount === totalRequired && !!signature;

  // Autosave: debounce PATCH by 500ms. Every field commit + signature
  // capture routes through here so we never lose work between saves.
  const queueSave = useCallback((patch) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.patch(`/documents/${did}`, patch);
        setSavedAt(new Date());
      } catch (e) {
        toast.error(e.response?.data?.detail || "Autosave failed");
      } finally { setSaving(false); }
    }, 500);
  }, [did]);

  const setFieldValue = (id, value) => {
    setValues((v) => {
      const next = { ...v, [id]: value };
      queueSave({ values: { [id]: value } });
      return next;
    });
  };

  const openField = (field) => {
    if (doc.status === "signed") return;
    if (field.type === "checkbox") { setFieldValue(field.id, !values[field.id]); return; }
    if (field.type === "signature") { setSignatureFieldId(field.id); setSigning(true); return; }
    if (field.type === "date" && !values[field.id]) {
      const today = new Date().toISOString().slice(0, 10);
      setEditingField({ ...field, prefill: today }); return;
    }
    setEditingField(field);
  };

  const commitField = (val) => {
    if (!editingField) return;
    setFieldValue(editingField.id, val);
    setEditingField(null);
    goToNextField();
  };

  const acceptSignature = (dataUrl) => {
    setSignature(dataUrl);
    queueSave({ signature_png: dataUrl, signature_field_id: signatureFieldId });
  };

  const goToNextField = () => {
    const idx = fields.findIndex((f) => f.id === (editingField?.id));
    if (idx < 0 || idx === fields.length - 1) return;
    const next = fields.slice(idx + 1).find((f) => !isDone(f));
    if (next) setPageIdx(next.page);
  };

  const isDone = (f) => {
    if (f.type === "signature") return !!signature;
    if (f.type === "checkbox") return values[f.id];
    return values[f.id] != null && String(values[f.id]).trim() !== "";
  };

  const jumpToField = (f) => { setPageIdx(f.page); setTimeout(() => openField(f), 100); };

  const complete = async () => {
    if (!readyToSign) { toast.error("Complete all required fields and capture the signature first"); return; }
    setCompleting(true);
    try {
      const { data } = await api.post(`/documents/${did}/complete`);
      toast.success("Agreement signed & locked");
      setDoc(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "missing_required_fields") {
        toast.error(`${detail.fields.length} required field(s) still missing`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Completion failed");
      }
    } finally { setCompleting(false); }
  };

  if (!doc) return <div className="p-6 text-[#5C738A]"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  const template = doc.template_name;
  const pdfUrl = tpl?.pdf_url ? (tpl.pdf_url.startsWith("http") ? tpl.pdf_url : `${BACKEND_URL}${tpl.pdf_url}`) : null;
  const locked = doc.status === "signed";
  const finalPdf = doc.final_pdf_url ? (doc.final_pdf_url.startsWith("http") ? doc.final_pdf_url : `${BACKEND_URL}${doc.final_pdf_url}`) : null;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="document-signer">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2 flex-shrink-0">
        <div>
          <button onClick={() => nav("/admin/documents")} data-testid="back-documents" className="text-xs text-[#5C738A] hover:text-[#012340] inline-flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Documents</button>
          <h1 className="font-heading text-2xl font-black text-[#012340] mt-1">{doc.name}</h1>
          <div className="text-xs text-[#5C738A]">{template} · {doc.status}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-[#5C738A]" data-testid="required-progress">
            {completedCount} of {totalRequired} required field{totalRequired === 1 ? "" : "s"} completed
          </div>
          <div className="text-[10px] text-[#5C738A]">
            {saving ? (<><CircleNotch size={10} className="inline animate-spin" /> Saving…</>) : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Draft"}
          </div>
          {locked ? (
            <a href={finalPdf} target="_blank" rel="noopener noreferrer" data-testid="download-signed" className="px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2">
              <DownloadSimple size={14} weight="bold" /> Download signed PDF
            </a>
          ) : (
            <button onClick={complete} disabled={!readyToSign || completing} data-testid="complete-lock" className={`px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2 ${readyToSign ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#e9eef3] text-[#5C738A]"} disabled:opacity-60`}>
              <Lock size={14} weight="bold" /> {completing ? "Locking…" : "Complete & Lock"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2 flex-shrink-0">
        <button disabled={pageIdx <= 0} onClick={() => setPageIdx((p) => p - 1)} data-testid="page-prev" className="text-xs text-[#5C738A] disabled:opacity-30 inline-flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Prev page</button>
        <div className="text-xs text-[#5C738A]">Page {pageIdx + 1} of {tpl?.page_count || "…"}</div>
        <button disabled={!tpl || pageIdx >= tpl.page_count - 1} onClick={() => setPageIdx((p) => p + 1)} data-testid="page-next" className="text-xs text-[#5C738A] disabled:opacity-30 inline-flex items-center gap-1">Next page <ArrowRight size={12} weight="bold" /></button>
        {totalRequired > 0 && !locked && (
          <button onClick={() => {
            const next = fields.find((f) => !isDone(f));
            if (next) jumpToField(next);
          }} data-testid="jump-next-required" className="ml-auto text-xs bg-[#012340] text-white px-3 py-1.5 rounded-sm font-bold uppercase tracking-[0.18em]">Next required field</button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 bg-[#f4f5f7] rounded-sm">
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div>
            {pdfUrl && (
              <PdfCanvas
                file={pdfUrl}
                pageIndex={pageIdx}
                width={pageWidth}
                onSize={setPageSize}
              >
                {(ps) => (
                  <div style={{ position: "absolute", inset: 0, pointerEvents: "auto" }}>
                    {pageFields.map((f) => {
                      const done = isDone(f);
                      const isSig = f.type === "signature";
                      const isChk = f.type === "checkbox";
                      const disp = getDisplay(f, values, signature);
                      return (
                        <button
                          key={f.id}
                          onClick={(e) => { e.stopPropagation(); openField(f); }}
                          data-testid={`signer-field-${f.id}`}
                          disabled={locked}
                          style={{
                            position: "absolute",
                            left: f.x * ps.width,
                            top: f.y * ps.height,
                            width: f.width * ps.width,
                            height: f.height * ps.height,
                            border: `2px ${done ? "solid" : "dashed"} ${done ? "#0d6b3f" : (f.required ? "#CFAB7A" : "#5C738A")}`,
                            background: done ? "rgba(13,107,63,0.08)" : "rgba(207,171,122,0.14)",
                            padding: 2,
                            cursor: locked ? "default" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "flex-start",
                            fontSize: 12, fontWeight: 700, color: "#012340",
                            overflow: "hidden",
                          }}
                        >
                          {isSig && signature && signatureFieldId === f.id ? (
                            <img src={signature} alt="signature" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                          ) : isChk ? (
                            <span style={{ fontSize: Math.max(f.height * ps.height * 0.7, 12), color: done ? "#0d6b3f" : "transparent" }}>✓</span>
                          ) : disp ? (
                            <span style={{ padding: "0 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{disp}</span>
                          ) : (
                            <span style={{ fontSize: 10, letterSpacing: "0.05em", padding: "0 4px", opacity: 0.75 }}>
                              {isSig ? "TAP HERE TO SIGN" : `Tap: ${f.name}${f.required ? " *" : ""}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </PdfCanvas>
            )}
          </div>
        </div>
      </div>

      <FieldEditorSheet
        open={!!editingField}
        field={editingField}
        initialValue={editingField ? (values[editingField.id] ?? editingField.prefill ?? "") : ""}
        onDone={commitField}
        onCancel={() => setEditingField(null)}
      />
      <SignaturePadModal
        open={signing}
        onClose={() => setSigning(false)}
        onAccept={acceptSignature}
        title={fields.find((f) => f.id === signatureFieldId)?.name || "Customer Signature"}
      />

      {locked && (
        <div className="fixed bottom-4 right-4 bg-[#0d6b3f] text-white px-4 py-3 rounded-sm shadow-lg text-sm inline-flex items-center gap-2 z-40" data-testid="locked-banner">
          <CheckCircle size={16} weight="bold" /> Signed & locked. This document is immutable.
        </div>
      )}
    </div>
  );
}

function getDisplay(f, values, signature) {
  if (f.type === "signature") return null;
  if (f.type === "checkbox") return null;
  return values[f.id];
}
